import classNames from '@/common/utils/classNames';
import { getDoctorProfile } from '@/common/apis/services/doctor/profile';
import { NativePanelKey, nativePanelHref } from '@/modules/doctorPanel/constants';
import { PANEL_KEY_SCOPE } from '@/modules/doctorPanel/secretary/scopes';
import { useActingDoctor, useActingScopes } from '@/modules/doctorPanel/secretary/gateway';
import { useQuery } from '@tanstack/react-query';
import moment from 'jalali-moment';
import { CalendarDays, CalendarOff, ChevronLeft, Clock, type LucideIcon } from 'lucide-react';
import Link from 'next/link';
import { DsCard, DsSectionHeader, ds } from '../designSystem';
import { DoctorHomeSheetLayoutProvider } from '../hooks/doctorHomeSheetLayout';
import { useSelectedDateStore } from '../store/selectedDate';
import { FeedAppointmentsSection } from './feedAppointmentsSection';
import { FeedGreeting } from './feedGreeting';
import { FeedMotion } from './feedMotion';

const ACTIONS: { key: NativePanelKey; icon: LucideIcon; title: string; subtitle: string }[] = [
  { key: 'appointments', icon: CalendarDays, title: 'مراجعین من', subtitle: 'نوبت‌ها، ثبت و لغو نوبت، یادداشت' },
  { key: 'workhours', icon: Clock, title: 'ساعت کاری', subtitle: 'دیدن و تغییر ساعت کاری مطب' },
  { key: 'vacation', icon: CalendarOff, title: 'مرخصی', subtitle: 'ثبت و حذف مرخصی' },
];

/** «منشی دکتر …»: the doctor's own name, read through the gateway (the profile call is routed to the doctor while acting). */
const useActingDoctorName = () => {
  const doctorId = useActingDoctor();
  const profile = useQuery(['secretary-acting-doctor', doctorId], () => getDoctorProfile(), { enabled: !!doctorId, staleTime: 10 * 60_000, retry: false });
  const name = [profile.data?.name, profile.data?.family].filter(Boolean).join(' ').trim();
  return name || null;
};

const SecretaryBanner = () => {
  const name = useActingDoctorName();
  return (
    <DsCard className="flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <p className={ds.type.cardTitle}>{name ? `منشی ${/^(دکتر|دکتر\s)/.test(name) ? name : `دکتر ${name}`}` : 'ورود به‌عنوان منشی'}</p>
        <p className={classNames(ds.type.caption, 'mt-0.5')}>فقط بخش‌هایی را می‌بینید که پزشک در اختیارتان گذاشته است.</p>
      </div>
    </DsCard>
  );
};

/** What this secretary may open, as the same kind of card the doctor home uses for its sections. */
const SecretaryActions = () => {
  const scopes = useActingScopes();
  const allowed = ACTIONS.filter(action => {
    const scope = PANEL_KEY_SCOPE[action.key];
    return !!scope && scopes.includes(scope);
  });
  if (allowed.length === 0) return null;
  return (
    <section dir="rtl" aria-label="امکانات شما">
      <DsSectionHeader title="امکانات شما" />
      <DsCard padding="none" className="overflow-hidden">
        {allowed.map((action, index) => (
          <Link
            key={action.key}
            href={nativePanelHref(action.key)}
            className={classNames('flex items-center gap-3 px-4 py-3 active:bg-black/[0.04]', index > 0 && 'border-t border-black/[0.06]')}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <action.icon size={20} aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className={classNames(ds.type.cardTitle, 'block truncate')}>{action.title}</span>
              <span className={classNames(ds.type.caption, 'mt-0.5 block truncate')}>{action.subtitle}</span>
            </span>
            <ChevronLeft size={18} className="shrink-0 text-slate-400" aria-hidden />
          </Link>
        ))}
      </DsCard>
    </section>
  );
};

/** the day's appointments only — no online-visit switch, wallet, reviews or tools (not part of a secretary's access) */
const SecretaryToday = () => {
  const scopes = useActingScopes();
  const selectedDate = useSelectedDateStore(state => state.selectedDate);
  if (!scopes.includes('appointments')) return null;
  const selectedMoment = moment(selectedDate, 'YYYY-MM-DD');
  const title = selectedMoment.isSame(moment(), 'day') ? 'برنامه امروز' : `برنامه ${selectedMoment.clone().locale('fa').format('jD jMMMM')}`;
  return (
    <section dir="rtl" aria-label={title}>
      <DsSectionHeader title={title} />
      <DsCard padding="none" className="overflow-hidden">
        <FeedAppointmentsSection nested />
      </DsCard>
    </section>
  );
};

/** The doctor home as a secretary sees it: the same layout and cards, with only what her access opens. */
export const SecretaryHomeFeed = ({ className }: { className?: string }) => (
  <main aria-label="صفحه اصلی منشی" className={classNames('flex flex-col', ds.layout.feedSectionGap, className)}>
    <DoctorHomeSheetLayoutProvider layout="mobile">
      <FeedMotion index={0}>
        <SecretaryBanner />
      </FeedMotion>
      <FeedMotion index={1}>
        <FeedGreeting hideStats />
      </FeedMotion>
      <FeedMotion index={2}>
        <SecretaryActions />
      </FeedMotion>
      <FeedMotion index={3}>
        <SecretaryToday />
      </FeedMotion>
    </DoctorHomeSheetLayoutProvider>
  </main>
);
